import {uuid} from 'uuidv4';
import React from 'react';
import {DOT, DASH, VARIABLE} from './types.js';

const ACCENT = '\'';
const LEGATO = '\,';
const STACCATO = '.';
const SPACE = ' ';
const NEW_LINE = '\n';
const BRACKET_LEFT = '[';
const BRACKET_RIGHT = ']';
const TILDA = '~';
const STAR = '*';
export const MOD = '%';
const RAMP = '^';
const FORWARD_SLASH = '/';

const newVar = (name) => ({
    type: VARIABLE,
    name 
});

const newLeaf = () => ({
    cycle: undefined,
    transpose: '',
    operand: '',
    operator: undefined,
    id: uuid()
});

const isEmptyLeaf = (leaf) => {
    return leaf.transpose === '';
};

export const parse = (str) => {
    str = ' ' + str + ' ';
    let current = []; // current cycle
    let stack = [current];
    let leaf = newLeaf();
    for (let i=0; i < str.length; i++) {
        let c = str.charAt(i);
        switch (c) {
        case STAR:
            if (leaf.transpose === '') {
                leaf.cycle = current.pop();
            }
            leaf.operator = '*';
            break;
        case MOD:
            if (leaf.transpose === '') {
                leaf.cycle = current.pop();
            }
            leaf.operator = MOD;
            break;
        case RAMP:
            if (leaf.transpose === '') {
                leaf.cycle = current.pop();
            }
            leaf.operator = RAMP;
            break;
        case FORWARD_SLASH:
            if (leaf.transpose === '') {
                leaf.cycle = current.pop();
            }
            leaf.operator = '/';
            break;
        case TILDA:
            leaf.transpose = null;
            break;
        case ACCENT:
            leaf.accent = true;
            break;
        case LEGATO:
            leaf.legato = true;
            break;
        case SPACE:
            completeLeaf(leaf, current);
            leaf = newLeaf();
            break;
        case NEW_LINE:
            completeLeaf(leaf, current);
            leaf = newLeaf();
            break;
        case BRACKET_LEFT:
            completeLeaf(leaf, current);
            current = [];
            stack.push(current);
            leaf = newLeaf();
            break;
        case BRACKET_RIGHT:
            completeLeaf(leaf, current);
            let old = stack.pop();
            current = stack[stack.length - 1] || [];
            current.push(old);
            leaf = newLeaf();
            break;
        default:
            if (c === STACCATO && !isEmptyLeaf(leaf)) {
                leaf.staccato = true;
            } else if (c === DOT && isEmptyLeaf(leaf)) {
                leaf.type = dot();
                leaf.transpose = '0';
            } else if (c === DASH && isEmptyLeaf(leaf)) {
                leaf.type = dash(0);
                leaf.transpose = '0';
                completeLeaf(leaf, current);
                leaf = newLeaf();
                leaf.type = dash(1);
                leaf.transpose = '0';
            }else {
                if (leaf.operator) {
                    leaf.operand += c;
                } else {
                    leaf.transpose += c;
                }
            }
        }
    }
    return stack[0];
};

function lastAdded(stack) {
    return stack[stack.length = 1] || [];

}

function completeLeaf(leaf, current) {
    if (leaf.cycle) {
        if (!isNaN(parseInt(leaf.operand))) {
            leaf.operand = parseInt(leaf.operand);
        } else {
            leaf.operand = newVar(leaf.operand);
        }
        current.push(leaf);
    } else if (leaf.transpose !== undefined && leaf.transpose !== "") {
        let transpose = null;
        if (leaf.transpose != null) {
            transpose = parseFloat(leaf.transpose);
            if (!isNaN(transpose)) {
                // its a number
                leaf.transpose =  transpose;
            } else {
                // it's a variable!
                leaf.transpose = newVar(leaf.transpose);
            }
        }

        if (!isNaN(parseInt(leaf.operand))) {
            leaf.operand = parseInt(leaf.operand);
        } else if (leaf.operand && leaf.operand !== "") {
            leaf.operand = newVar(leaf.operand);
        }
        current.push(leaf);
    }
}

export const getCycleSpans = (cycle, currentLeaves, returnIndex) => {
    let spans = [];
    let str = "";
    let index = undefined;
    for (let i=0; i < cycle.length; i++) {
        if (Array.isArray(cycle[i])) {
            str += "[";
            let ret = getCycleSpans(cycle[i], currentLeaves, true);
            let _spans = ret.spans;
            if (ret.index !== undefined) {
                index = spans.length + ret.index;
            }
            str += _spans[0];
            if (_spans.length >= 1) {
                spans.push(str[str.length - 1] !== "[" && str.trim() !== "[" ? str.trim() + " " : str.trim());
                spans = [... spans, ..._spans.slice(1)];
                spans[spans.length - 1] = spans[spans.length - 1].trim();
                if (spans[spans.length - 2]) {
                    let _str = spans[spans.length - 2].trim(); 
                    if (_str[_str.length - 1] === ']') {
                        spans[spans.length - 2] = _str;
                    }
                }
                spans[spans.length - 1] += "] ";
                str = "";
            }  else {
                str = str.trim();
                spans[spans.length - 1] = spans[spans.length - 1].trim();
                str += "] ";
            }
        } else {
            let push = false;
            if (currentLeaves && currentLeaves.some(l => l.id === cycle[i].id)) {
                push = true;
                if (spans.length === 0) {
                    spans.push(str);
                } else {
                    spans[spans.length - 1] += str;
                }
                str = "";
            } 
            str += getLeafString(cycle[i]);
            if (i < cycle.length - 1 && str[str.length-1] !== ' ') {
                str += ' ';
            }
            if (push) {
                index = spans.length;
                spans.push(str);
                str = " ";
            }
        }
    }
    let x = [... spans, str];
    let y = x;
    if (y[1] === "") {
        y.splice(1, 1);
        index--;
    }
    let last;
    let z = [];
    for (let term of y) {
        if (last && last[last.length - 1] === ']') {
            if (term[0] !== " " && term !== "]" && term !== "[") {
                term = " " + term;
            }
            if (term.trim()[0] === "]") {
                term = term.trim();
            }
        }
        z.push(term);
        last = term;
    }
    if (returnIndex) {
        return {spans: z, index};
    } else {
        return z;
    }
};

export const getCycleString = (cycle) => {
    let str = "";
    for (let i=0; i < cycle.length; i++) {
        if (Array.isArray(cycle[i])) {
            let cstr = getCycleString(cycle[i]).trim();
            str += "[" + cstr + "] ";
        } else {
            str += getLeafString(cycle[i]);
            if (i < cycle.length - 1 && str[str.length-1] !== ' ') {
                str += ' ';
            }
        }
    }
    return str;
}

export const getLeafString = (leaf, skipOperator=false) => {
    if (leaf.cycle) {
        let cstr = getCycleString(leaf.cycle).trim();
        return "[" + cstr + "]" + leaf.operator + getOperandString(leaf.operand);
    }
    let x = '';
    if (leaf.operator) {
        let leafStr = leaf.transpose;
        if (leafStr == null) {
            leafStr = "~";
        } else if (typeof leaf.transpose === "object") {
            leafStr = leaf.transpose.name;
        } else {
            leafStr = leaf.transpose;
        }
        x = skipOperator ? leafStr : leafStr + leaf.operator + getOperandString(leaf.operand);
    } else if (typeof leaf.transpose === "object") {
        x = leaf.transpose === null ? "~" : leaf.transpose.name;
    } else {
        if (leaf.type) {
            let {type, value} = leaf.type;
            if (type=== DOT) {
                x = '.';
            } else if (type === DASH && value === 0) {
                x = '-';
            } else {
                x = '';
            }
        } else {
            x = leaf.transpose;
        }
    }
    if (leaf.accent) {
        x += ACCENT;
    } else if (leaf.staccato) {
        x += STACCATO;
    } else if (leaf.legato) {
        x += LEGATO;
    }
    return x;
};

const getOperandString = (x) => {
    if (x === "") {
        return undefined;
    }
    return typeof x === "object" ? x.name : x;
};



export const dot = () => ({
    type: DOT
});

export const dash = (value) => ({
    type: DASH,
    value
});
